import { useEffect, useState } from 'react';
import Dexie, { type EntityTable } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import type { AtomId, LearnerNote, MasteryEvidence, MasteryVector, ReviewCard } from '@cpu/domain';

export interface AtomProgressRecord {
  atomId: AtomId;
  openedAt: string;
  lastVisitedAt: string;
  completedLessonIds: string[];
  /** True when the learner ticks the atom as finished. */
  done?: 0 | 1;
  /** Local-time `YYYY-MM-DD` of the tick, for honest ledgers. */
  doneAt?: string;
}

export interface BookmarkRecord {
  id?: number;
  atomId: AtomId;
  createdAt: string;
}

export interface MasteryRecord {
  atomId: AtomId;
  vector: MasteryVector;
  evidenceCount: number;
  updatedAt: string;
}

export interface RecentItemRecord {
  href: string;
  title: string;
  type: 'module' | 'atom' | 'lesson' | 'problem' | 'set';
  visitedAt: string;
}

export interface AttemptRecord {
  id?: number;
  problemId: string;
  body: string;
  updatedAt: string;
}

export type FollowupStatus = 'attempted' | 'revealed';

export interface FollowupProgressRecord {
  /** `${moduleId}/${setId}/${questionId}` */
  id: string;
  moduleId: string;
  setId: string;
  questionId: string;
  status: FollowupStatus;
  updatedAt: string;
}

export interface DailyVisitRecord {
  /** `YYYY-MM-DD` in local time. */
  day: string;
  /** Number of tracked activities that day (visits, reviews, attempts…). */
  activity: number;
  firstAt: string;
  lastAt: string;
}

export class ProgressDatabase extends Dexie {
  atomProgress!: EntityTable<AtomProgressRecord, 'atomId'>;
  mastery!: EntityTable<MasteryRecord, 'atomId'>;
  evidence!: EntityTable<MasteryEvidence, 'id'>;
  reviewCards!: EntityTable<ReviewCard, 'id'>;
  notes!: EntityTable<LearnerNote, 'id'>;
  bookmarks!: EntityTable<BookmarkRecord, 'id'>;
  recentItems!: EntityTable<RecentItemRecord, 'href'>;
  attempts!: EntityTable<AttemptRecord, 'id'>;
  followupProgress!: EntityTable<FollowupProgressRecord, 'id'>;
  dailyActivity!: EntityTable<DailyVisitRecord, 'day'>;
  xpEvents!: EntityTable<XpEventRecord, 'id'>;
  focusSessions!: EntityTable<FocusSessionRecord, 'id'>;
  scratchPad!: EntityTable<ScratchPadRecord, 'id'>;
  atomState!: EntityTable<AtomStateRecord, 'atomId'>;

  constructor() {
    super('cp-ultimate-trace');
    this.version(1).stores({
      atomProgress: '&atomId, lastVisitedAt',
      mastery: '&atomId, updatedAt',
      evidence: '&id, atomId, kind, dimension, createdAt',
      reviewCards: '&id, atomId, dueAt',
      notes: '++id, atomId, updatedAt, *tags',
      bookmarks: '++id, &atomId, createdAt',
      recentItems: '&href, type, visitedAt'
    });
    this.version(2).stores({
      attempts: '++id, &problemId, updatedAt',
      followupProgress: '&id, moduleId, setId, status, updatedAt'
    });
    this.version(3).stores({
      dailyActivity: '&day, lastAt'
    });
    this.version(4).stores({
      xpEvents: '++id, day, kind, createdAt',
      focusSessions: '++id, day, completedAt',
      scratchPad: '++id, createdAt, cleared'
    });
    this.version(5).stores({
      atomState: '&atomId, updatedAt'
    });
  }
}

/** Per-atom learner state that is neither a visit nor mastery evidence. */
export interface AtomStateRecord {
  atomId: AtomId;
  /** 0/1 (IndexedDB booleans survive, but integers index cleanly). */
  done: 0 | 1;
  doneAt: string;
  updatedAt: string;
}

export const progressDb = new ProgressDatabase();

export const emptyMasteryVector = (): MasteryVector => ({
  recognize: 0,
  explain: 0,
  trace: 0,
  implement: 0,
  apply: 0
});

export async function recordAtomVisit(atomId: AtomId): Promise<void> {
  const now = new Date().toISOString();
  const current = await progressDb.atomProgress.get(atomId);
  await progressDb.atomProgress.put({
    atomId,
    openedAt: current?.openedAt ?? now,
    lastVisitedAt: now,
    completedLessonIds: current?.completedLessonIds ?? []
  });
  await recordDailyActivity(1);
  // Honest effort ledger: an atom earns XP once per local day, not per reload.
  const openedToday = await progressDb.xpEvents
    .where('day').equals(dayKey(new Date()))
    .filter((event) => event.kind === 'atom' && (event as { atomId?: string }).atomId === atomId)
    .count();
  if (!openedToday) {
    await progressDb.xpEvents.add({ kind: 'atom', weight: XP_WEIGHTS.atom, day: dayKey(new Date()), createdAt: now, atomId });
  }
}

export async function addRecentItem(item: Omit<RecentItemRecord, 'visitedAt'>): Promise<void> {
  await progressDb.recentItems.put({ ...item, visitedAt: new Date().toISOString() });
}

/**
 * One note per atom, period. Saving again replaces the atom's note body
 * (and bumps updatedAt) instead of stacking an unbounded history — the old
 * multi-note-per-atom behaviour made the library useless.
 */
export async function saveNote(atomId: AtomId, body: string, tags: string[] = []): Promise<number> {
  const now = new Date().toISOString();
  const existing = await progressDb.notes.where('atomId').equals(atomId).first();
  if (existing?.id != null) {
    await progressDb.notes.update(existing.id, { body, tags, updatedAt: now });
    return existing.id;
  }
  const id = await progressDb.notes.add({ atomId, body, tags, createdAt: now, updatedAt: now });
  if (id == null) throw new Error('The note database did not return an id.');
  return id;
}

export async function deleteNote(id: number): Promise<void> {
  await progressDb.notes.delete(id);
}

export function useAllNotes(): LearnerNote[] {
  return useLiveQuery(
    () => progressDb.notes.orderBy('updatedAt').reverse().toArray(),
    [],
    []
  );
}

export async function setAtomDone(atomId: AtomId, done: boolean): Promise<void> {
  const now = new Date().toISOString();
  if (done) {
    await progressDb.atomState.put({ atomId, done: 1, doneAt: now, updatedAt: now });
    await recordXp('complete', undefined, new Date(now));
    await recordDailyActivity(1, new Date(now));
    return;
  }
  await progressDb.atomState.put({ atomId, done: 0, doneAt: '', updatedAt: now });
}

export function useAtomDone(atomId: string): boolean {
  const record = useLiveQuery(
    () => progressDb.atomState.get(atomId as AtomId),
    [atomId]
  );
  return record?.done === 1;
}

export function useDoneAtomIds(): Set<string> {
  const records = useLiveQuery(
    () => progressDb.atomState.filter((record) => record.done === 1).toArray(),
    [],
    []
  );
  return new Set((records ?? []).map((record) => record.atomId));
}

/** Done-atom counts keyed by module slug (e.g. `m13` → 7). */
export function useDoneAtomCountsByModule(): Map<string, number> {
  const records = useLiveQuery(
    () => progressDb.atomState.filter((record) => record.done === 1).toArray(),
    [],
    []
  );
  const map = new Map<string, number>();
  for (const record of records ?? []) {
    const moduleSlug = `m${record.atomId.slice(0, 2)}`;
    map.set(moduleSlug, (map.get(moduleSlug) ?? 0) + 1);
  }
  return map;
}

export async function toggleBookmark(atomId: AtomId): Promise<boolean> {
  const existing = await progressDb.bookmarks.where('atomId').equals(atomId).first();
  if (existing?.id != null) {
    await progressDb.bookmarks.delete(existing.id);
    return false;
  }
  await progressDb.bookmarks.add({ atomId, createdAt: new Date().toISOString() });
  return true;
}

export function useBookmarkedAtomIds(): Set<string> {
  const bookmarks = useLiveQuery(() => progressDb.bookmarks.toArray(), [], []);
  return new Set((bookmarks ?? []).map((bookmark) => bookmark.atomId));
}

export function useAtomNotes(atomId: string): LearnerNote[] {
  return useLiveQuery(
    () => progressDb.notes.where('atomId').equals(atomId).sortBy('updatedAt'),
    [atomId],
    []
  ).slice().reverse();
}

export async function saveAttempt(problemId: string, body: string): Promise<void> {
  const now = new Date().toISOString();
  const existing = await progressDb.attempts.where('problemId').equals(problemId).first();
  if (existing?.id != null) {
    await progressDb.attempts.update(existing.id, { body, updatedAt: now });
    return;
  }
  await progressDb.attempts.add({ problemId, body, updatedAt: now });
}

export function useAttempt(problemId: string): AttemptRecord | undefined {
  return useLiveQuery(
    () => progressDb.attempts.where('problemId').equals(problemId).first(),
    [problemId]
  );
}

export function useAttemptedProblemIds(): Set<string> {
  const attempts = useLiveQuery(() => progressDb.attempts.toArray(), [], []);
  return new Set((attempts ?? []).map((attempt) => attempt.problemId));
}

export type FollowupProgressMap = Record<string, FollowupStatus>;

export function followupProgressKey(moduleId: string, setId: string, questionId: string): string {
  return `${moduleId}/${setId}/${questionId}`;
}

export async function recordFollowupProgress(
  moduleId: string,
  setId: string,
  questionId: string,
  status: FollowupStatus
): Promise<void> {
  await progressDb.followupProgress.put({
    id: followupProgressKey(moduleId, setId, questionId),
    moduleId,
    setId,
    questionId,
    status,
    updatedAt: new Date().toISOString()
  });
}

export function useModuleFollowupProgress(moduleId: string): FollowupProgressMap {
  const records = useLiveQuery(
    () => progressDb.followupProgress.where('moduleId').equals(moduleId).toArray(),
    [moduleId],
    []
  );
  const map: FollowupProgressMap = {};
  for (const record of records ?? []) map[record.questionId] = record.status;
  return map;
}

export function useAttemptedSetIds(moduleId: string): Set<string> {
  const records = useLiveQuery(
    () => progressDb.followupProgress.where('moduleId').equals(moduleId).toArray(),
    [moduleId],
    []
  );
  return new Set((records ?? []).map((record) => record.setId));
}

// ---------------------------------------------------------------------------
// Review scheduling (pure logic, FSRS-flavored: stability × difficulty)
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

export type ReviewRating = 'forgot' | 'hard' | 'good' | 'easy';

export interface NewReviewCardInput {
  id: string;
  atomId: AtomId;
  prompt: string;
  answer: string;
  now?: Date;
}

export function newReviewCard({ id, atomId, prompt, answer, now = new Date() }: NewReviewCardInput): ReviewCard {
  return {
    id,
    atomId,
    prompt,
    answer,
    dueAt: now.toISOString(),
    stability: 0,
    difficulty: 0.3,
    reps: 0,
    lapses: 0
  };
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * Pure FSRS-flavored update. Interval grows with stability; 'forgot' halves it
 * and registers a lapse; 'hard' grows stability slowly; 'easy' skips ahead.
 */
export function applyReview(card: ReviewCard, rating: ReviewRating, now = new Date()): ReviewCard {
  const difficultyDelta = { forgot: 0.12, hard: 0.03, good: -0.02, easy: -0.08 }[rating];
  const stabilityGain = { forgot: 0, hard: 0.4, good: 1, easy: 1.9 }[rating];
  const stability =
    rating === 'forgot'
      ? Math.max(0.4, card.stability * 0.5)
      : Math.max(0.7, card.stability + stabilityGain * (1.25 - card.difficulty));
  const difficulty = clamp(card.difficulty + difficultyDelta, 0.1, 0.9);
  const intervalDays = Math.max(0.02, stability * (1 - difficulty));
  return {
    ...card,
    stability,
    difficulty,
    reps: card.reps + 1,
    lapses: card.lapses + (rating === 'forgot' ? 1 : 0),
    dueAt: new Date(now.getTime() + intervalDays * DAY_MS).toISOString()
  };
}

export async function addReviewCard(input: NewReviewCardInput): Promise<void> {
  await progressDb.reviewCards.put(newReviewCard(input));
}

export async function reviewCard(cardId: string, rating: ReviewRating): Promise<void> {
  const card = await progressDb.reviewCards.get(cardId);
  if (!card) throw new Error(`Review card ${cardId} does not exist.`);
  await progressDb.reviewCards.put(applyReview(card, rating));
}

export async function deleteReviewCard(cardId: string): Promise<void> {
  await progressDb.reviewCards.delete(cardId);
}

export function useRecentItems(limit = 5): RecentItemRecord[] {
  return useLiveQuery(
    () => progressDb.recentItems.orderBy('visitedAt').reverse().limit(limit).toArray(),
    [limit],
    []
  );
}

/** Module ids (e.g. `M13`) the learner has touched via any recent item. */
export function useVisitedModuleIds(): Set<string> {
  const items = useLiveQuery(
    () => progressDb.recentItems.toArray(),
    [],
    []
  );
  const ids = new Set<string>();
  for (const item of items ?? []) {
    const match = item.href.match(/m(\d{2})/);
    if (match) ids.add(`M${match[1]}`);
  }
  return ids;
}

/** Per-atom visit counts keyed by module id (e.g. `m13` → atomId → visits). */
export function useVisitedAtomCountsByModule(): Map<string, Map<string, number>> {
  const records = useLiveQuery(() => progressDb.atomProgress.toArray(), [], []);
  const map = new Map<string, Map<string, number>>();
  for (const record of records ?? []) {
    // Atom ids are `NN.NN.NN`; the module slug is `m` + the first segment.
    const moduleSlug = `m${record.atomId.slice(0, 2)}`;
    let inner = map.get(moduleSlug);
    if (!inner) {
      inner = new Map();
      map.set(moduleSlug, inner);
    }
    inner.set(record.atomId, (inner.get(record.atomId) ?? 0) + 1);
  }
  return map;
}

export function useDueReviewCards(): ReviewCard[] {
  // A fresh Date per render would change the query deps every render and loop
  // forever; sample the clock once per minute instead.
  const [nowIso, setNowIso] = useState(() => new Date().toISOString());
  useEffect(() => {
    const timer = window.setInterval(() => setNowIso(new Date().toISOString()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return useLiveQuery(
    () => progressDb.reviewCards.where('dueAt').belowOrEqual(nowIso).sortBy('dueAt'),
    [nowIso],
    []
  );
}

export function useAllReviewCards(): ReviewCard[] {
  return useLiveQuery(() => progressDb.reviewCards.toArray(), [], []);
}

/** Distinct recent-item types the learner has produced (atom, lesson, set…). */
export function useRecentTypes(): Set<string> {
  const items = useLiveQuery(() => progressDb.recentItems.toArray(), [], []);
  return new Set((items ?? []).map((item) => item.type));
}

export function useNoteCount(): number {
  return useLiveQuery(() => progressDb.notes.count(), [], 0);
}

export function useFollowupProgressCount(): number {
  return useLiveQuery(() => progressDb.followupProgress.count(), [], 0);
}

// ---------------------------------------------------------------------------
// Daily activity & streaks
// ---------------------------------------------------------------------------

/** Local-time `YYYY-MM-DD` key for a date. */
export function dayKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export async function recordDailyActivity(weight = 1, now = new Date()): Promise<void> {
  const day = dayKey(now);
  const existing = await progressDb.dailyActivity.get(day);
  if (existing) {
    await progressDb.dailyActivity.update(day, { activity: existing.activity + weight, lastAt: now.toISOString() });
    return;
  }
  await progressDb.dailyActivity.put({ day, activity: weight, firstAt: now.toISOString(), lastAt: now.toISOString() });
}

export function useDailyActivity(days = 182): DailyVisitRecord[] {
  return useLiveQuery(async () => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return progressDb.dailyActivity.where('day').aboveOrEqual(dayKey(cutoff)).toArray();
  }, [days], []);
}

/**
 * Pure streak computation. A streak counts consecutive local days with
 * activity, ending today (if active) or yesterday (grace — today isn't over).
 */
export function computeStreak(days: Set<string>, now = new Date()): { current: number; longest: number; activeToday: boolean } {
  if (days.size === 0) return { current: 0, longest: 0, activeToday: false };
  const today = dayKey(now);
  const yesterday = dayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
  const activeToday = days.has(today);

  let cursor = activeToday ? new Date(now.getFullYear(), now.getMonth(), now.getDate()) : new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  let current = 0;
  while (days.has(dayKey(cursor))) {
    current += 1;
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 1);
  }
  if (!activeToday && current === 0) {
    // Nothing yesterday either; today still possible but streak not started.
    current = 0;
  }

  const sorted = [...days].sort();
  let longest = 0;
  let run = 0;
  let previous: string | null = null;
  for (const day of sorted) {
    if (previous != null) {
      const [y, m, d] = previous.split('-').map(Number);
      const prevDate = new Date(y!, (m ?? 1) - 1, d ?? 1);
      prevDate.setDate(prevDate.getDate() + 1);
      run = dayKey(prevDate) === day ? run + 1 : 1;
    } else {
      run = 1;
    }
    longest = Math.max(longest, run);
    previous = day;
  }
  return { current, longest: Math.max(longest, current), activeToday };
}

export function useStreak(): { current: number; longest: number; activeToday: boolean } {
  const activity = useDailyActivity(730);
  return computeStreak(new Set(activity.map((record) => record.day)));
}

export async function exportProgress(): Promise<Blob> {
  const payload = {
    version: 3,
    exportedAt: new Date().toISOString(),
    atomProgress: await progressDb.atomProgress.toArray(),
    mastery: await progressDb.mastery.toArray(),
    evidence: await progressDb.evidence.toArray(),
    reviewCards: await progressDb.reviewCards.toArray(),
    notes: await progressDb.notes.toArray(),
    bookmarks: await progressDb.bookmarks.toArray(),
    recentItems: await progressDb.recentItems.toArray(),
    attempts: await progressDb.attempts.toArray(),
    followupProgress: await progressDb.followupProgress.toArray(),
    dailyActivity: await progressDb.dailyActivity.toArray(),
    xpEvents: await progressDb.xpEvents.toArray(),
    focusSessions: await progressDb.focusSessions.toArray(),
    scratchPad: await progressDb.scratchPad.toArray()
  };
  return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
}

// ---------------------------------------------------------------------------
// Effort ledger (XP), focus sessions & scratch pad — all honest, all local
// ---------------------------------------------------------------------------

export type XpKind = 'atom' | 'lab' | 'review' | 'attempt' | 'focus' | 'note' | 'complete';

export interface XpEventRecord {
  id?: number;
  kind: XpKind;
  weight: number;
  /** Local-time `YYYY-MM-DD`. */
  day: string;
  createdAt: string;
  /** Present on `atom` events for once-per-day dedupe. */
  atomId?: string;
}

export interface FocusSessionRecord {
  id?: number;
  day: string;
  minutes: number;
  label: string;
  completedAt: string;
}

export interface ScratchPadRecord {
  id?: number;
  body: string;
  cleared: 0 | 1;
  createdAt: string;
}

/** XP awarded per honest action — deliberately small numbers. */
export const XP_WEIGHTS: Record<XpKind, number> = {
  atom: 2,
  lab: 5,
  review: 3,
  attempt: 3,
  focus: 0, // focus sessions award `Math.round(minutes / 5)` at completion
  note: 2,
  complete: 5
};

export async function recordXp(kind: XpKind, weightOverride?: number, now = new Date()): Promise<number> {
  const weight = weightOverride ?? XP_WEIGHTS[kind];
  if (weight <= 0) return 0;
  await progressDb.xpEvents.add({ kind, weight, day: dayKey(now), createdAt: now.toISOString() });
  return weight;
}

export function useXpToday(): number {
  const events = useLiveQuery(
    () => progressDb.xpEvents.where('day').equals(dayKey(new Date())).toArray(),
    [],
    []
  );
  return (events ?? []).reduce((sum, event) => sum + event.weight, 0);
}

/** Daily XP totals for the last `days` days, keyed `YYYY-MM-DD` (zero-filled). */
export function useXpSeries(days = 105): Map<string, number> {
  const records = useLiveQuery(async () => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return progressDb.xpEvents.where('day').aboveOrEqual(dayKey(cutoff)).toArray();
  }, [days], []);
  const series = new Map<string, number>();
  if (records) for (const record of records) series.set(record.day, (series.get(record.day) ?? 0) + record.weight);
  return series;
}

export async function recordFocusSession(minutes: number, label: string, now = new Date()): Promise<void> {
  await progressDb.focusSessions.add({ day: dayKey(now), minutes, label, completedAt: now.toISOString() });
  await recordXp('focus', Math.max(1, Math.round(minutes / 5)), now);
}

export function useFocusSessionsToday(): FocusSessionRecord[] {
  return useLiveQuery(
    () => progressDb.focusSessions.where('day').equals(dayKey(new Date())).toArray(),
    [],
    []
  ).slice().reverse();
}

export function useFocusMinutesToday(): number {
  const sessions = useFocusSessionsToday();
  return sessions.reduce((sum, session) => sum + session.minutes, 0);
}

export async function addScratchItem(body: string): Promise<void> {
  const trimmed = body.trim();
  if (!trimmed) return;
  await progressDb.scratchPad.add({ body: trimmed, cleared: 0, createdAt: new Date().toISOString() });
}

export function useScratchItems(): ScratchPadRecord[] {
  return useLiveQuery(
    () => progressDb.scratchPad.where('cleared').equals(0).sortBy('createdAt'),
    [],
    []
  ).slice().reverse();
}

export async function clearScratchItem(id: number): Promise<void> {
  await progressDb.scratchPad.update(id, { cleared: 1 });
}

/** First-run ritual state. The first step ships pre-checked (endowed progress). */
export interface RitualState {
  done: string[];
}

const RITUAL_KEY = 'cp-ultimate-ritual';

export function loadRitual(): RitualState {
  try {
    const raw = localStorage.getItem(RITUAL_KEY);
    const done = raw ? (JSON.parse(raw) as string[]) : [];
    return { done: Array.isArray(done) ? done : ['open'] };
  } catch {
    return { done: ['open'] };
  }
}

export function saveRitualStep(step: string): RitualState {
  const state = loadRitual();
  if (!state.done.includes(step)) state.done.push(step);
  localStorage.setItem(RITUAL_KEY, JSON.stringify(state.done));
  return state;
}
